import '@material-design-icons/font';
import './themes/reset.scss';
import './themes/variables.scss';
import './i18n';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import axios from 'axios';
import { useKioskParams } from 'hooks/useKioskParams';
import type { ReactNode } from 'react';
import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes as ReactRoutes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import AnalyticsServiceFactory from 'services/analyticsService';
import GeoServiceFactory from 'services/geoService';
import type { AnalyticsEnvironment } from 'services/types/analytics';

import FaviconChanger from './components/shared/faviconSetter/FaviconSetter';
import { AppProvider } from './context/app';
import { NewRelicAgentProvider } from './context/newRelicAgent';
import { ThemeProviderCustomized } from './context/theme';
import CampusDirectory from './pages/CampusDirectory';
import CampusNavigation from './pages/CampusNavigation';
import reportWebVitals from './reportWebVitals';
import DiscoveryServiceFactory from './services/discoveryService';
import { ROUTES } from './utils/routes';

const queryClient = new QueryClient();

DiscoveryServiceFactory(
  process.env.REACT_APP_DISCOVER_API_BASE_URL || '',
  process.env.REACT_APP_CUSTOMER_ENV || '',
  axios,
);

GeoServiceFactory(process.env.REACT_APP_AZURE_MAPS_PRIMARY_KEY || '');

const AnalyticsServiceInitializer = () => {
  const params = useKioskParams();
  const location = useLocation();
  const prevLocation = useRef<string | undefined>(undefined);

  useEffect(() => {
    const analyticsService = AnalyticsServiceFactory(
      (process.env.REACT_APP_BUILD_ENV || '') as AnalyticsEnvironment,
      process.env.REACT_APP_KIOSK_MODE === 'true' ? 'kiosk' : 'web',
      process.env.REACT_APP_CUSTOMER_ENV || '',
      params?.id ?? '',
    );

    if (prevLocation.current !== location?.pathname) {
      analyticsService.track(analyticsService.events.PageLoad);
      prevLocation.current = location?.pathname;
    }
  }, [location, params?.id]);

  return null;
};

export default AnalyticsServiceInitializer;

interface EnsureKioskQueryParamsProps {
  children: ReactNode;
}

const EnsureKioskQueryParams: React.FC<EnsureKioskQueryParamsProps> = ({
  children,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id: kioskId, model: kioskModel } = useKioskParams() || {};

  useEffect(() => {
    if (process.env.REACT_APP_KIOSK_MODE === 'true' && kioskId && kioskModel) {
      const searchParams = new URLSearchParams(location.search);

      if (kioskId) searchParams.set('kioskId', kioskId);
      if (kioskModel) searchParams.set('kioskModel', kioskModel);
      navigate(`${location.pathname}?${searchParams.toString()}`, {
        replace: true,
      });
    }
  }, [location.search, location.pathname, navigate, kioskId, kioskModel]);

  return <>{children}</>;
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
);

const Routes = (
  <ReactRoutes>
    <Route path={ROUTES.CAMPUS_NAVIGATION} element={<CampusNavigation />} />
    <Route path={ROUTES.CAMPUS_DIRECTORY} element={<CampusDirectory />} />
    <Route
      path="*"
      element={<Navigate to={ROUTES.CAMPUS_DIRECTORY} replace />}
    />
  </ReactRoutes>
);

root.render(
  <React.StrictMode>
    <NewRelicAgentProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProviderCustomized>
          <AppProvider>
            <FaviconChanger />
            <BrowserRouter>
              <EnsureKioskQueryParams>
                <AnalyticsServiceInitializer />
                {Routes}
              </EnsureKioskQueryParams>
            </BrowserRouter>
            <ReactQueryDevtools initialIsOpen={false} />
          </AppProvider>
        </ThemeProviderCustomized>
      </QueryClientProvider>
    </NewRelicAgentProvider>
  </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
